<template>
    <DCard
        padding
        main-class="overflow-x-auto overflow-visible"
    >
        <div class="search">
            <DRow>
                <DCol sm="2">
                    <h6>
                        {{ __('products.products.price_plan_coverage_view.filters.search') }}
                    </h6>
                </DCol>
                <DCol sm="2">
                    <h6>
                        {{ __('products.products.price_plan_coverage_view.filters.subtypes') }}
                    </h6>
                </DCol>
                <DCol sm="2">
                    <h6>
                        {{ __('products.products.price_plan_coverage_view.filters.product_classes') }}
                    </h6>
                </DCol>
                <DCol sm="2">
                    <h6>
                        {{ __('products.products.price_plan_coverage_view.filters.product_groups') }}
                    </h6>
                </DCol>
                <DCol sm="2">
                    <h6>
                        {{ __('products.products.price_plan_coverage_view.filters.retired') }}
                    </h6>
                </DCol>
            </DRow>
            <DRow>
                <DCol sm="2">
                    <DInput
                        id="search"
                        v-model="filter.search"
                        :placeholder="__('products.products.filter.search')"
                        @keydown.enter.exact.prevent="submitFilters"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="subtypes-dropdown"
                        :options="subtypesOptions"
                        :search="true"
                        :selection="true"
                        :options-empty-placeholder="__('products.products.price_plan_coverage_view.filters.subtypes')"
                        selection-type="text"
                        @update="updateSelectedSubtypes"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="product-classes-dropdown"
                        :options="productClassesOptions"
                        :search="true"
                        :selection="true"
                        :options-empty-placeholder="__('products.products.price_plan_coverage_view.filters.product_classes')"
                        selection-type="text"
                        @update="updateSelectedProductClasses"
                    />
                </DCol>
                <DCol sm="2">
                    <DMultiSelect
                        id="product-groups-dropdown"
                        :options="productGroupsOptions"
                        :search="true"
                        :selection="true"
                        :options-empty-placeholder="__('products.products.price_plan_coverage_view.filters.product_groups')"
                        selection-type="text"
                        @update="updateSelectedProductGroups"
                    />
                </DCol>
                <DCol sm="1">
                    <DSwitch
                        id="retirement"
                        color="success"
                        variant="3d"
                        :checked="shouldIncludeRetired"
                        @update:checked="updateRetired"
                    />
                </DCol>
                <DCol sm="3">
                    <DButton
                        id="filter-apply"
                        :label="__('products.products.price_plan_coverage_view.filters.filter')"
                        @click="submitFilters"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('products.products.price_plan_coverage_view.filters.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </div>
        <DRow class="mt-2">
            <DCol col="12">
                <h6>{{ __('products.products.price_plan_coverage_view.title') }}</h6>
                <p>
                    <DIcon
                        :id="`check_circle`"
                        name="check-circle"
                        :size="20"
                        class="color-green"
                    />
                    {{ __('products.products.price_plan_coverage_view.helper_text_product') }}
                </p>
                <p>
                    <DIcon
                        :id="`minus_circle`"
                        name="minus-circle"
                        :size="20"
                        class="color-red"
                    />
                    {{ __('products.products.price_plan_coverage_view.helper_text_plan') }}
                </p>
            </DCol>
        </DRow>
        <DTable
            :fields="fields"
            :items="items"
            class="table-responsive"
        >
            <template
                v-for="field in fields"
                :key="field"
                #[field]="{ item }"
            >
                <td v-if="field === 'product'">
                    <span v-if="userPermissions.includes('product.view')">
                        <Link :href="productRoute(item)">
                            {{ item.name }}
                        </Link>
                    </span>
                    <span v-else>
                        {{ item.name }}
                    </span>
                    <DBadge
                        v-if="item.retired"
                        color="info"
                    >
                        {{ __('products.products.index.retired') }}
                    </DBadge>
                </td>
                <td v-else-if="item[field] === 'no_plan'">
                    <DIcon
                        :id="`minus_circle_${item['field']}`"
                        name="minus-circle"
                        :size="20"
                        class="color-red"
                    />
                    ({{ __('products.products.price_plan_coverage_view.no_plan') }})
                </td>
                <td v-else-if="item[field] === 'not_public'">
                    <DIcon
                        :id="`minus_circle_${item['field']}`"
                        name="minus-circle"
                        :size="20"
                        class="color-red"
                    />
                    ({{ __('products.products.price_plan_coverage_view.not_public') }})
                </td>
                <td v-else-if="item[field] === 'no_prices'">
                    <DIcon
                        :id="`minus_circle_${item['field']}`"
                        name="minus-circle"
                        :size="20"
                        class="color-red"
                    />
                    ({{ __('products.products.price_plan_coverage_view.no_prices') }})
                </td>
                <td v-else>
                    <DIcon
                        :id="`check_circle_${item['column']}`"
                        name="check-circle"
                        :size="20"
                        class="color-green"
                    />
                </td>
            </template>
        </DTable>
    </DCard>
</template>

<script setup>
import { computed } from 'vue'
import { route } from '~@/Composables/route'
import { router, usePage } from '@inertiajs/vue3'

const props = defineProps({
    fields: {
        type: Array,
        default: () => { return [] }
    },
    items: {
        type: Object,
        default: () => { return {} }
    },
    subtypes: {
        type: Array,
        default () {
            return []
        }
    },
    productClasses: {
        type: Array,
        default () {
            return []
        }
    },
    productGroups: {
        type: Array,
        default () {
            return []
        }
    },
    filterData: {
        type: Object,
        default () {
            return {}
        }
    },
})

const userPermissions = computed(() => usePage().props.auth.permissions)

const filter = {
    search: (props.filterData !== null && props.filterData.search) || getParam('search'),
    subtypes: (props.filterData !== null && props.filterData.subtypes) || getParam('subtypes'),
    productClasses: (props.filterData !== null && props.filterData.productClasses) || getParam('productClasses'),
    productGroups: (props.filterData !== null && props.filterData.productGroups) || getParam('productGroups'),
    retired: (props.filterData !== null && props.filterData.retired) || getParam('retired') || 0
}

const shouldIncludeRetired = computed(() => Number(filter.retired) !== 0)

const subtypesOptions = props.subtypes.map((item) => {
    return {
        value: item.value,
        text: item.label,
        selected: props.filterData?.subtypes?.includes(String(item.value))
    }
})

const productClassesOptions = props.productClasses.map((item) => {
    return {
        value: item.value,
        text: item.label,
        selected: props.filterData?.productClasses?.includes(String(item.value))
    }
})

const productGroupsOptions = props.productGroups.map((item) => {
    return {
        value: item.value,
        text: item.label,
        selected: props.filterData?.productGroups?.includes(String(item.value))
    }
})

function updateSelectedSubtypes (values) {
    filter.subtypes = values
}

function updateSelectedProductClasses (values) {
    filter.productClasses = values
}

function updateSelectedProductGroups (values) {
    filter.productGroups = values
}

function updateRetired (value) {
    filter.retired = value ? 1 : 0
}

function productRoute (value) {
    return route('products.products.show', { product: value.id })
}

function submitFilters () {
    router.get(route('products.products.products-price-plan-coverage', {
        _query: filter,
        saveFilters: true
    }))
}

function resetFilters () {
    router.get(route('products.products.products-price-plan-coverage', {
        saveFilters: true,
        defaultFilters: true
    }))
}

function getParam (paramName) {
    const params = new URLSearchParams(window.location.search)
    return params.has(paramName) ? params.get(paramName) : null
}

</script>

<style lang="scss" scoped>
.color-green {
    color: green;
}
.color-red {
    color: red;
}
.search {
    padding: 7px 7px 7px 14px;
    border-bottom: var(--colorAppBorder) solid 1px;
}
</style>
